/**
 * One saved eval run: totals per arm, checks by arm, cases by arm, the outputs of a
 * selected case, and the human review. A finished run can be re-graded from here; a
 * re-grade shows its report against the source run. The selected case and arm are in the
 * query string, so a view can be shared.
 */
import { ArrowLeft, RefreshCw, RotateCcw, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '../../components/Button';
import ErrorDisplay from '../../components/ErrorDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';
import { ArmSummaryTable } from './components/run/ArmSummaryTable';
import { CaseDetail } from './components/run/CaseDetail';
import { CaseMatrix } from './components/run/CaseMatrix';
import { RegradeForm } from './components/run/RegradeForm';
import { RegradeReportPanel } from './components/run/RegradeReportPanel';
import { RegradesList } from './components/run/RegradesList';
import { ReviewPanel } from './components/run/ReviewPanel';
import { RunHeader } from './components/run/RunHeader';
import { SuggestionsPanel } from './components/run/SuggestionsPanel';
import { useEvalRunDetail } from './hooks/useEvalRunDetail';
import { isActiveRun } from './types/evalTypes';
import { sumSplits, trialCost, type CostSplit } from './utils/evalCost';
import { planRerun } from './utils/rerun';
import { cellKey } from './utils/evalFormat';

export const EvalRun = () => {
  const { runId = '' } = useParams<{ runId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { detail, catalog, loading, error, reload, saveReview, saveJudgeFlag, decideSuggestion, cancelRun } = useEvalRunDetail(runId);

  const navigate = useNavigate();
  const [showRegrade, setShowRegrade] = useState(false);

  const selectedCaseId = searchParams.get('case');
  const selectedArmId = searchParams.get('arm');

  // Bring the case into view when one is picked from the matrix.
  useEffect(() => {
    if (selectedCaseId) {
      document.getElementById('case-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedCaseId]);

  const flagged = useMemo(() => (detail?.results ?? []).filter((result) => result.judgeFlag !== null), [detail]);
  const flaggedCells = useMemo(() => new Set(flagged.map((result) => cellKey(result.caseId, result.armId))), [flagged]);

  const caseCosts = useMemo(() => {
    const models = catalog?.models ?? [];
    const byCase = new Map<string, CostSplit[]>();
    for (const result of detail?.results ?? []) {
      byCase.set(result.caseId, [...(byCase.get(result.caseId) ?? []), trialCost(models, result)]);
    }
    return new Map([...byCase].map(([caseId, costs]) => [caseId, sumSplits(costs)]));
  }, [detail, catalog]);

  if (loading) return <LoadingSpinner message="Loading the eval run…" />;

  const suite = catalog?.suites.find((candidate) => candidate.id === detail?.run.suiteId);
  const models = catalog?.models ?? [];
  const cases = detail?.cases ?? [];

  // Checked here only to grey out the button; the Evals page plans the load again.
  const rerun = detail && catalog ? planRerun(detail, suite, models) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <Link to="/evals" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
            <ArrowLeft size={14} /> Evals
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" icon={<RefreshCw size={13} />} onClick={() => void reload()}>
              Refresh
            </Button>
            {detail && !isActiveRun(detail.run.status) && detail.results.some((result) => result.output) && (
              <Button
                variant="outline"
                size="sm"
                icon={<RotateCcw size={13} />}
                title="Grade these outputs again with a judge prompt version, in a new run"
                onClick={() => setShowRegrade(true)}
              >
                Re-grade…
              </Button>
            )}
            {rerun && (
              <Button
                variant="primary"
                size="sm"
                icon={<Upload size={13} />}
                disabled={!rerun.ok}
                title={rerun.ok ? "Load this run's setup into the new-run form" : rerun.reason}
                onClick={() => navigate(`/evals?load=${runId}`)}
              >
                Load setup
              </Button>
            )}
          </div>
        </div>

        {rerun && !rerun.ok && <p className="text-sm text-amber-800">Cannot load this setup: {rerun.reason}</p>}

        {error && <ErrorDisplay message={error} />}

        {detail && (
          <>
            <RunHeader run={detail.run} models={models} productionModel={suite?.productionModel} prompts={detail.prompts} onCancel={cancelRun} />

            {showRegrade && <RegradeForm detail={detail} suite={suite} models={models} onClose={() => setShowRegrade(false)} />}

            <RegradeReportPanel run={detail.run} onShow={(caseId, armId) => setSearchParams({ case: caseId, arm: armId }, { replace: true })} />

            <RegradesList regrades={detail.regrades} />

            <SuggestionsPanel
              suggestions={detail.suggestions}
              results={detail.results}
              currentUid={user?.uid}
              onShow={(caseId, armId) => setSearchParams({ case: caseId, arm: armId }, { replace: true })}
              onDecide={decideSuggestion}
            />

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Checks by arm</h2>
              <ArmSummaryTable run={detail.run} results={detail.results} models={models} productionModel={suite?.productionModel} />
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Cases by arm</h2>
              <p className="text-sm text-gray-500 mb-2">
                Trials that passed every check. Select a cell to see the outputs.
                {flagged.length > 0 && ` ${flagged.length} trial${flagged.length === 1 ? ' has' : 's have'} a judge flag.`}
              </p>
              <CaseMatrix
                run={detail.run}
                models={models}
                productionModel={suite?.productionModel}
                cases={cases}
                flaggedCells={flaggedCells}
                caseCosts={caseCosts}
                selectedCaseId={selectedCaseId}
                selectedArmId={selectedArmId}
                onSelect={(caseId, armId) => setSearchParams({ case: caseId, arm: armId }, { replace: true })}
              />
            </section>

            {selectedCaseId && detail.run.caseIds.includes(selectedCaseId) && (
              <CaseDetail
                suiteId={detail.run.suiteId}
                caseId={selectedCaseId}
                caseInfo={cases.find((candidate) => candidate.id === selectedCaseId)}
                arms={detail.run.arms}
                productionModel={suite?.productionModel}
                results={detail.results}
                models={models}
                selectedArmId={selectedArmId}
                currentUid={user?.uid}
                onSaveFlag={saveJudgeFlag}
                suggestions={detail.suggestions}
                onDecideSuggestion={decideSuggestion}
              />
            )}

            {/* Keyed by run, so a refreshed review resets the form. */}
            <ReviewPanel key={`${detail.run.runId}-${detail.run.review?.reviewedAt ?? 'none'}`} review={detail.run.review} currentUid={user?.uid} onSave={saveReview} />
          </>
        )}
      </div>
    </div>
  );
};
